import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Marker } from 'react-leaflet';
import { useDispatch } from 'react-redux';
import { observationService } from '@iso/services';
import { IconObservation } from '@iso/containers/Pages/PropertyMap/Icons/IconObservation';
import modalActions from '@iso/redux/modal/actions';

const parseCoordinate = (value) => {
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const resolvePoint = (observation) => {
  const directLat = parseCoordinate(observation?.lat);
  const directLng = parseCoordinate(observation?.lng);

  if (directLat !== null && directLng !== null) {
    return [directLat, directLng];
  }

  const pointLat = parseCoordinate(observation?.point?.lat ?? observation?.point?.latitude);
  const pointLng = parseCoordinate(observation?.point?.lng ?? observation?.point?.longitude);

  if (pointLat !== null && pointLng !== null) {
    return [pointLat, pointLng];
  }

  return null;
};

const filterActiveObservations = (items = []) => {
  const now = Date.now();
  return items.filter((item) => {
    const isApproved = item?.latest_approval?.status === 'approved';
    if (!isApproved) {
      return false;
    }
    if (!item?.expires_at) {
      return true;
    }
    const expiresAt = Date.parse(item.expires_at);
    if (Number.isNaN(expiresAt)) {
      return true;
    }
    return expiresAt > now;
  });
};

const ObservationMarkers = ({ propertyId, onLocationsChange, reloadKey = 0 }) => {
  const [observations, setObservations] = useState([]);
  const dispatch = useDispatch();

  const loadObservations = useCallback(async () => {
    if (!propertyId) {
      setObservations([]);
      return;
    }

    try {
      const res = await observationService.getList(propertyId, { only_active: 1 });
      if (res?.code === '0000') {
        setObservations(filterActiveObservations(res.observations || []));
      } else {
        setObservations([]);
      }
    } catch (error) {
      setObservations([]);
    }
  }, [propertyId]);

  useEffect(() => {
    loadObservations();
  }, [loadObservations, reloadKey]);

  useEffect(() => {
    if (typeof onLocationsChange === 'function') {
      const points = observations
        .map((observation) => resolvePoint(observation))
        .filter(Boolean);
      onLocationsChange(points);
    }
  }, [observations, onLocationsChange]);

  const handleMarkerClick = useCallback((observation) => {
    if (!observation) return;
    dispatch(
      modalActions.openModal({
        object: observation,
        type: 'observation',
      })
    );
  }, [dispatch]);

  const renderMarkers = useMemo(() => (
    observations.map((observation) => {
      const position = resolvePoint(observation);
      if (!position) {
        return null;
      }
      return (
        <Marker
          key={`health-safety-observation-${observation.id}`}
          position={position}
          icon={IconObservation()}
          riseOnHover
          onClick={() => handleMarkerClick(observation)}
        />
      );
    })
  ), [observations, handleMarkerClick]);

  return <>{renderMarkers}</>;
};

export default ObservationMarkers;
